// Design tokens do Design System BioAILab (paleta navy + dark "GitHub-like").
// getDesignTokens(mode) -> options prontas para createTheme(). mode = 'light' | 'dark'.
import { alpha } from '@mui/material/styles';

// Tints (50/100) derivados do "main" de cada cor semântica, usados por trechos do app
// que já referenciam `color.50` / `color.100` (ex.: bgcolor: 'primary.50') esperando um
// fundo levemente tingido — sem isso o valor não resolve e o fundo fica sem efeito.
const withTints = (color) => ({
    ...color,
    50: alpha(color.main, 0.08),
    100: alpha(color.main, 0.16),
});

const grey = {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
};

// Base compartilhada entre light e dark (dark só sobrescreve primary/secondary/warning).
const basePalette = {
    primary: { main: '#0E223B', light: '#516275', dark: '#071221', contrastText: '#FFFFFF' },
    secondary: { main: '#516275', light: '#767A82', dark: '#15202B', contrastText: '#FFFFFF' },
    success: { main: '#2E7D32', light: '#81C784', dark: '#00391A', contrastText: '#FFFFFF' },
    warning: { main: '#F57C00', light: '#FFB74D', dark: '#3A2300', contrastText: '#FFFFFF' },
    info: { main: '#1976D2', light: '#90CAF9', dark: '#082338', contrastText: '#FFFFFF' },
    error: { main: '#BA1A1A', light: '#FFB4AB', dark: '#690005', contrastText: '#FFFFFF' },
    grey,
};

export const getDesignTokens = (mode) => ({
    palette: {
        mode,
        ...(mode === 'light'
            ? {
                  ...basePalette,
                  primary: withTints(basePalette.primary),
                  secondary: withTints(basePalette.secondary),
                  success: withTints(basePalette.success),
                  warning: withTints(basePalette.warning),
                  info: withTints(basePalette.info),
                  error: withTints(basePalette.error),
                  background: {
                      default: '#F8F9FA',
                      paper: '#FFFFFF',
                  },
                  text: {
                      primary: '#1B1B1F',
                      secondary: '#454A52',
                      disabled: '#767A82',
                  },
              }
            : {
                  ...basePalette,
                  primary: withTints({ main: '#58A6FF', light: '#79C0FF', dark: '#1F6FEB', contrastText: '#FFFFFF' }),
                  secondary: withTints({ main: '#8B949E', light: '#ADB5BD', dark: '#6E7681', contrastText: '#FFFFFF' }),
                  warning: withTints({ main: '#A0621A', light: '#C07830', dark: '#6B3D0A', contrastText: '#FFFFFF' }),
                  success: withTints(basePalette.success),
                  info: withTints(basePalette.info),
                  error: withTints(basePalette.error),
                  background: {
                      default: '#0D1117',
                      paper: '#161B22',
                  },
                  text: {
                      primary: '#E6EDF3',
                      secondary: '#9BA3AD',
                      disabled: '#636B76',
                  },
                  divider: 'rgba(48, 54, 61, 0.8)',
              }),
    },
    typography: {
        fontFamily: ['Manrope', 'sans-serif'].join(','),
        h5: {
            fontWeight: 700,
        },
        h6: {
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
            },
            defaultProps: {
                disableElevation: true,
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: ({ theme }) => ({
                    boxShadow:
                        theme.palette.mode === 'light'
                            ? 'rgba(149, 157, 165, 0.1) 0px 8px 24px'
                            : 'none',
                    border:
                        theme.palette.mode === 'dark'
                            ? '1px solid rgba(48, 54, 61, 0.8)'
                            : 'none',
                }),
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? '#060A10'
                            : theme.palette.primary.main,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    border: 'none',
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light,
                    },
                }),
            },
        },
        // Sidebar (Drawer) sem borda/sombra própria — evita um "risco" visível na
        // junção com a AppBar (ambos navy), tratando os dois como uma única faixa.
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    border: 'none',
                    boxShadow: 'none',
                },
            },
        },
        // --- Overrides globais adicionais: aplicam os padrões de doc 03 (cards, menus,
        // navegação, dialogs) na plataforma inteira via tema, sem precisar editar cada tela. ---
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius * 3,
                    boxShadow: theme.palette.mode === 'light' ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                }),
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius * 2,
                    paddingTop: 4,
                    paddingBottom: 4,
                }),
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    minHeight: 40,
                    borderRadius: theme.shape.borderRadius * 1.5,
                    marginLeft: 4,
                    marginRight: 4,
                    marginTop: 2,
                    marginBottom: 2,
                    paddingLeft: 12,
                    paddingRight: 12,
                    '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                    },
                }),
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius * 1.5,
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
                        '& .MuiListItemText-primary': { fontWeight: 700 },
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
                    },
                }),
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius * 1.5,
                }),
            },
        },
    },
});
